import { useForm } from 'react-hook-form';
import { useMilestoneTemplates } from '@/hooks/useMilestoneTemplates';
import { useEventTypes } from '@/hooks/useEventTypes';
import { useTaskTypes } from '@/hooks/useTaskTypes';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface MilestoneTemplateFormProps {
  salesItemId: string;
  template?: any;
  onSuccess?: () => void;
}

export function MilestoneTemplateForm({ salesItemId, template, onSuccess }: MilestoneTemplateFormProps) {
  const { createTemplate, updateTemplate } = useMilestoneTemplates();
  const { eventTypes } = useEventTypes();
  const { taskTypes } = useTaskTypes();
  const { register, handleSubmit, setValue } = useForm<any>({
    defaultValues: template || {}
  });

  const onSubmit = async (data: any) => {
    const payload = {
      ...data,
      sales_item_id: salesItemId,
      milestone_order: parseInt(data.milestone_order),
      estimated_duration_days: data.estimated_duration_days ? parseInt(data.estimated_duration_days) : null,
    };

    if (template) {
      await updateTemplate.mutateAsync({ id: template.id, ...payload });
    } else {
      await createTemplate.mutateAsync(payload);
    }
    onSuccess?.();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div>
        <Label>Milestone Name</Label>
        <Input {...register('name', { required: true })} placeholder="Ex: Documentation Complete" />
      </div>

      <div>
        <Label>Description</Label>
        <Textarea {...register('description')} placeholder="Describe this milestone..." />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Order</Label>
          <Input type="number" {...register('milestone_order', { required: true })} placeholder="1" />
        </div>
        <div>
          <Label>Estimated Duration (days)</Label>
          <Input type="number" {...register('estimated_duration_days')} placeholder="30" />
        </div>
      </div>

      <div>
        <Label>Auto-Complete on Event (optional)</Label>
        <Select onValueChange={(value) => setValue('auto_complete_on_event_code', value || null)}>
          <SelectTrigger>
            <SelectValue placeholder="None" />
          </SelectTrigger>
          <SelectContent>
            {eventTypes.map((type) => (
              <SelectItem key={type.id} value={type.code}>
                {type.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Auto-Complete on Task (optional)</Label>
        <Select onValueChange={(value) => setValue('auto_complete_on_task_code', value || null)}>
          <SelectTrigger>
            <SelectValue placeholder="None" />
          </SelectTrigger>
          <SelectContent>
            {taskTypes.map((type) => (
              <SelectItem key={type.id} value={type.code}>
                {type.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Button type="submit" className="w-full" disabled={createTemplate.isPending || updateTemplate.isPending}>
        {(createTemplate.isPending || updateTemplate.isPending) ? 'Saving...' : template ? 'Update Milestone' : 'Create Milestone'}
      </Button>
    </form>
  );
}
